<template>
    <tbody ref="body">
        <slot name="headerRows"></slot>
        <tr style="height: 0px; border: none;">
            <infinite-loading ref="top" @infinite="topHandler" :identifier="identifier" direction="top">
                <template v-slot:no-results>
                    <span></span>
                </template>
                <template v-slot:no-more>
                    <span></span>
                </template>
                <template v-slot:spinner>
                    <v-progress-circular indeterminate></v-progress-circular>
                </template>
            </infinite-loading>
        </tr>
        <slot name="rows" :shownData="shownData"></slot>
        <tr style="height: 0px; border: none;">
            <infinite-loading ref="bottom" @infinite="bottomHandler" :identifier="identifier" direction="bottom">
                <template v-slot:no-results>
                    <span></span>
                </template>
                <template v-slot:no-more>
                    <span></span>
                </template>
                <template v-slot:spinner>
                    <v-progress-circular indeterminate></v-progress-circular>
                </template>
            </infinite-loading>
        </tr>
        <slot name="footerRows"></slot>
    </tbody>
</template>

<script>
import InfiniteLoading from 'vue-infinite-loading';

export default {
    components: {
        InfiniteLoading
    },
    props: {
        data: Array,
        pageSize: Number
    },
    data(){
        return {
            topIndex: 0,
            bottomIndex: 0,
            shownData: [],
            identifier: new Date().getTime()
        }
    },
    watch: {
        data(current, previous){
            if(current.length != previous.length){
                // Hard reset

                // Clear all the data
                this.topIndex = 0;
                this.bottomIndex = 0;
                this.shownData.splice(0, this.shownData.length);

                this.$nextTick(() => {
                    this.$refs.top.scrollParent.scrollTop = 0;

                    // Reset the loaders
                    this.identifier = new Date().getTime();
                });
            } else {
                // Soft reset

                // Calculate which item is visible at the top of the scrolled to (e.g. visible) area
                let currentScrollTop = this.$refs.top.scrollParent.scrollTop;
                let cumulativeHeight = 0;
                let cumulativeItems = 0;
                let targetTopIndex = null;
                let children = this.$refs.body.children;
                for(let i = 0; i < children.length; i++){
                    cumulativeHeight += children[i].clientHeight;

                    // We are specifically only counting children in the body slot
                    if(children[i].tagName == "TR"){
                        cumulativeItems++;
                    }
                    
                    if(cumulativeHeight > currentScrollTop){
                        // We have now passed the scroll top, which means the previous item is the top most
                        // item we can see
                        targetTopIndex = this.topIndex + cumulativeItems - 2;
                        break;
                    }
                }

                // Ensure we aren't trying to go above the top of the page
                if(targetTopIndex < 0){
                    targetTopIndex = 0;
                }

                // Reset the data to view a page starting from the current top most item we can see
                this.topIndex = targetTopIndex;
                this.bottomIndex = targetTopIndex + this.pageSize;
                let newData = this.data.slice(this.topIndex, this.bottomIndex);
                if(newData.length < this.bottomIndex - this.topIndex){
                    this.bottomIndex = this.topIndex + newData.length;
                }
                this.shownData.splice(0, this.shownData.length, ...newData);

                // Ensure that the current scroll position is maintained
                this.$nextTick(() => {
                    this.$refs.top.scrollParent.scrollTop = 0;

                    // Reset the loaders
                    this.identifier = new Date().getTime();
                });
            }
        }
    },
    methods: {
        topHandler($state){
            // Prevent going beyond 0
            let start = this.topIndex - this.pageSize;
            if(start < 0){
                start = 0;
            }

            // Fetch another page before the current top index
            let newData = this.data.slice(
                start, 
                this.topIndex
            );

            // Decrement the new top index by however much data we fetched
            this.topIndex -= newData.length;

            if(newData.length > 0){
                // Put the new data at the start
                this.shownData.splice(0, 0, ...newData);

                // Maintain scroll top
                let scrollHeight = this.$refs.top.scrollParent.scrollHeight;
                this.$nextTick(() => {
                    this.$refs.top.scrollParent.scrollTop += this.$refs.top.scrollParent.scrollHeight - scrollHeight;
                    $state.loaded();
                })
            } else {
                $state.complete();
            }
        },
        bottomHandler($state){
            // Fetch another page after the current bottom index
            let newData = this.data.slice(
                this.bottomIndex, 
                this.bottomIndex + this.pageSize
            );

            // Increment the new bottom index by however much data we fetched
            this.bottomIndex += newData.length;

            if(newData.length > 0){
                // Put the new data at the end
                this.shownData.push(...newData);
                $state.loaded();
            } else {
                $state.complete();
            }
        }
    }
}
</script>